
function About() {
  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <h1 className="text-3xl font-bold tracking-tight mb-6 font-serif">About readwell</h1>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-3">Accessible Classic Literature for K-12 Schools</h2>
        <p className="text-muted-foreground leading-relaxed">
          Readwell provides WCAG 2.1 AA conformant classic literature for K-12 schools — accessible
          by default, in text, audio, and bimodal formats. VPAT 2.4 Rev INT and accessibility
          documentation included for your procurement team.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-3">The Problem</h2>
        <p className="text-muted-foreground leading-relaxed mb-3">
          The April 2024 ADA Title II Final Rule requires all educational content to meet
          WCAG 2.1 Level AA accessibility standards by April 2026/2027. This includes:
        </p>
        <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4 mb-3">
          <li>Teacher-uploaded PDFs of classic literature</li>
          <li>Links to Project Gutenberg and other free sources</li>
          <li>Summer reading lists and independent reading materials</li>
          <li>Library catalogs and digital resources</li>
        </ul>
        <p className="text-muted-foreground leading-relaxed">
          A typical mid-sized school district has 400,000-500,000 non-compliant classic
          literature PDFs, each representing a potential $3,500+ violation.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-3">The Solution</h2>
        <p className="text-muted-foreground leading-relaxed mb-3">
          Readwell provides universal access to WCAG 2.1 AA conformant versions of ~67% of Common Core
          Text Exemplars (100% of the public domain portion), featuring:
        </p>
        <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
          <li><strong className="text-foreground">Bimodal Reading</strong> - Text synchronized with professional audio narration</li>
          <li><strong className="text-foreground">Word-Level Highlighting</strong> - Words highlight as they're spoken</li>
          <li><strong className="text-foreground">Full Keyboard Navigation</strong> - Complete keyboard-only control</li>
          <li><strong className="text-foreground">Screen Reader Support</strong> - Semantic HTML with ARIA labels</li>
          <li><strong className="text-foreground">Anonymous Access</strong> - No student accounts required (Zero-PII)</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-3">Why Not Learning Ally?</h2>
        <p className="text-muted-foreground leading-relaxed mb-3">
          Learning Ally is excellent for IEP accommodations, but they operate under the
          Chafee Amendment (Section 121 of the Copyright Act), which legally restricts
          them to students with documented disabilities.
        </p>
        <p className="text-muted-foreground leading-relaxed mb-3">
          The ADA Title II rule requires <strong className="text-foreground">proactive accessibility</strong> - content
          must be accessible to all students when a teacher posts it, not after a student
          requests accommodation.
        </p>
        <p className="text-muted-foreground leading-relaxed mb-3">
          <strong className="text-foreground">Schools need both:</strong>
        </p>
        <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
          <li><strong className="text-foreground">Learning Ally</strong> - Reactive accommodation for IEP students (copyrighted modern books)</li>
          <li><strong className="text-foreground">Readwell</strong> - Proactive accessibility for all students (public domain classics)</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-3">Pricing</h2>
        <p className="text-muted-foreground leading-relaxed mb-2">
          District-wide unlimited access: <strong className="text-foreground">$5,000-15,000/year</strong>
        </p>
        <p className="text-muted-foreground leading-relaxed mb-2">
          Pilot year (5-10 books of your choice): <strong className="text-foreground">$2,000</strong>
        </p>
        <p className="text-sm text-muted-foreground italic">
          VPAT 2.4 Rev INT and accessibility documentation come in the welcome packet.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-3">Fast Procurement</h2>
        <p className="text-muted-foreground leading-relaxed">
          Our Zero-PII architecture means no student accounts, no FERPA audit trails,
          and minimal DPA requirements. Procurement time: <strong className="text-foreground">2-4 weeks</strong> (vs. 3-6 months
          for traditional ed-tech products).
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-3">Contact</h2>
        <p className="text-muted-foreground leading-relaxed">
          For pilot inquiries or questions, email us at{' '}
          <a
            href="mailto:info@readwell.academy"
            className="text-primary hover:underline"
          >
            info@readwell.academy
          </a>{' '}
          or visit{' '}
          <a
            href="https://www.readwellaudio.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline"
          >
            readwellaudio.com
          </a>.
        </p>
      </section>
    </div>
  );
}

export default About;
